import type { PopoverOrigin } from '@mui/material';
import { Box, IconButton, Popover, Stack, styled, Typography } from '@mui/material';
import dayjs from 'dayjs';
import type { MouseEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CloseIcon } from '@/src/shared/assets/CloseIcon';
import type { RootState } from '@/src/store';
import { darkGrey, labelColor } from '@/src/styles/theme/lightThemeOptions';

const DefaultAnchorOrigin: PopoverOrigin = {
    vertical: 'center',
    horizontal: 'right',
};
const DefaultTransformOrigin: PopoverOrigin = {
    vertical: 'center',
    horizontal: 'left',
};

interface GreatingPopoverProps {
    open: boolean;
    anchorEl?: Element;
    onClose: () => void;
}

const StyledTypography = styled(Typography)<{
    color?: string;
    fontSize?: string;
    fontWeight?: string;
}>(({ color, fontSize, fontWeight }) => ({
    fontFamily: 'Equip_Regular',
    fontSize: fontSize || '14px',
    fontWeight: fontWeight || '400',
    color: color || labelColor,
}));

export const useGreatingtPopover = () => {
    const [greatingPopoverAnchorEl, setGreatingtPopoverAnchorEl] = useState<
        HTMLElement | undefined
    >(undefined);
    const openGreatingtPopover = useCallback((event: MouseEvent<HTMLElement>) => {
        setGreatingtPopoverAnchorEl(event.currentTarget);
    }, []);
    const closeGreatingtPopover = useCallback(() => {
        setGreatingtPopoverAnchorEl(undefined);
    }, []);

    return { greatingPopoverAnchorEl, openGreatingtPopover, closeGreatingtPopover };
};

const WrapperStack = styled(Stack)({
    gap: '8px',
    fontFamily: 'Equip_Regular',
});

export const GreatingPopover = ({ open, anchorEl, onClose }: GreatingPopoverProps) => {
    const { t } = useTranslation('common');
    const formattedDate = dayjs().format('DD/MMM/YYYY').replace('.', '');
    const user = useSelector((state: RootState) => state.user['state/user']);

    return (
        <Popover
            anchorOrigin={DefaultAnchorOrigin}
            transformOrigin={DefaultTransformOrigin}
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            sx={{
                '& .MuiPaper-elevation': {
                    width: '503px',
                    padding: '20px',
                    borderRadius: '16px',
                    top: '85px !important',
                    left: '275px !important',
                    '@media (max-width: 991px)': {
                        top: '72px !important',
                        left: '136px !important',
                    },
                    '@media (max-width: 767px)': {
                        height: '618px',
                        top: '68px !important',
                        left: '16px !important',
                    },
                },
            }}
        >
            <WrapperStack>
                <Box
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <StyledTypography color={darkGrey} fontSize="18px" fontWeight="700">
                        {t('greatingHeading')}
                    </StyledTypography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <StyledTypography
                    dangerouslySetInnerHTML={{
                        __html: t('greatingMessage', {
                            date: formattedDate,
                            name: user?.givenName,
                        }),
                    }}
                ></StyledTypography>
            </WrapperStack>
        </Popover>
    );
};
